/** The service said no, in the service's own words.
 *
 * `src/lib/http.ts` writes every `reason` for a person to read — one clause,
 * no stack, no ids the reader cannot see — so the honest thing is to put it
 * on the page rather than translate it into something vaguer. The `code` is
 * shown small beside it: it is what a bug report needs and what the logger
 * branches on.
 *
 * The note is an `alert`, so it is announced when it appears rather than
 * waiting to be found, and it offers a retry whenever the caller has one.
 */

import { ApiError, errorMessage } from '@/lib/api';
import type { ReactNode } from 'react';

export interface ErrorNoteProps {
  /** Whatever was thrown. `ApiError` is unpacked; anything else is read for
   *  its message. */
  error: unknown;
  /** Overrides the leading line — "could not load the tracks". */
  title?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  compact?: boolean;
  className?: string;
}

export function ErrorNote({
  error,
  title,
  onRetry,
  retryLabel = 'Try again',
  compact,
  className,
}: ErrorNoteProps) {
  const code = error instanceof ApiError ? error.code : null;
  const status = error instanceof ApiError ? error.status : null;

  return (
    <div
      role="alert"
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: compact ? '8px 10px' : '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid color-mix(in srgb, var(--color-error) 45%, transparent)',
        background: 'color-mix(in srgb, var(--color-error) 8%, transparent)',
        fontSize: 13,
        textWrap: 'pretty',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--color-error)',
          flex: 'none',
          marginTop: 7,
        }}
      />
      <span style={{ minWidth: 0, flex: 1 }}>
        {title ? <span style={{ display: 'block' }}>{title}</span> : null}
        <span style={{ display: 'block', color: 'var(--color-neutral-300)' }}>
          {errorMessage(error)}
        </span>
        {code ? (
          <span
            style={{
              display: 'block',
              marginTop: 2,
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-neutral-600)',
            }}
          >
            {status} {code}
          </span>
        ) : null}
      </span>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" style={{ flex: 'none' }} onClick={onRetry}>
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}

export default ErrorNote;
